/**
 * useNfse — data layer da emissão de NFS-e (Fase 1, Trilho Nacional).
 *
 * Lista as notas da empresa ativa (Realtime), e expõe as ações que passam
 * SEMPRE pelas Edge Functions (o servidor é a autoridade: numeração, assinatura,
 * transmissão e ciclo de vida). O cliente só cria RASCUNHO e dispara a emissão.
 */
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import type { FiscalCertificate, NfseDocument, NfseEvent } from '@/types/fiscal';

export interface NewEmissionInput {
  provider: 'nacional' | 'sao_paulo' | 'fortaleza';
  client_id?: string | null;
  /** Orçamento de origem, quando a nota é emitida a partir de um orçamento. */
  quote_id?: string | null;
  // jsonb no banco → Json (o tipo do Supabase). `Record<string, unknown>` NÃO é
  // atribuível a Json (unknown ≠ Json), o que fazia o overload de .insert() falhar.
  tomador_snapshot: Json;
  servico_snapshot: Json;
  valor_servico: number;
  deducoes?: number;
  aliquota_iss?: number | null;
  iss_retido?: boolean;
  competencia?: string | null;
  share_with_client?: boolean;
}

async function invokeCode<T = unknown>(fn: string, body: unknown): Promise<{ data: T | null; code: string | null }> {
  const { data, error } = await supabase.functions.invoke(fn, { body });
  if (error) {
    let code = 'erro';
    try {
      const ctx = (error as { context?: Response }).context;
      if (ctx && typeof ctx.json === 'function') code = (await ctx.json())?.code ?? code;
    } catch { /* mantém genérico */ }
    return { data: null, code };
  }
  return { data: data as T, code: null };
}

export function useNfse() {
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['nfse-documents', companyId],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<NfseDocument[]> => {
      const { data, error } = await supabase
        .from('nfse_documents')
        .select('*')
        .eq('company_id', companyId as string)
        .is('discarded_at', null)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  // Realtime: qualquer mudança nas notas da empresa refaz a lista.
  useEffect(() => {
    if (!companyId) return;
    const channel = supabase
      .channel(`nfse-${companyId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'nfse_documents', filter: `company_id=eq.${companyId}` },
        () => qc.invalidateQueries({ queryKey: ['nfse-documents', companyId] }),
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [companyId, qc]);

  const issue = useMutation({
    mutationFn: async (input: NewEmissionInput) => {
      if (!companyId) throw new Error('Nenhuma empresa ativa');
      const { data: draft, error } = await supabase
        .from('nfse_documents')
        .insert({
          company_id: companyId,
          provider: input.provider,
          status: 'draft',
          client_id: input.client_id ?? null,
          quote_id: input.quote_id ?? null,
          tomador_snapshot: input.tomador_snapshot,
          servico_snapshot: input.servico_snapshot,
          valor_servico: input.valor_servico,
          deducoes: input.deducoes ?? 0,
          aliquota_iss: input.aliquota_iss ?? null,
          iss_retido: input.iss_retido ?? false,
          competencia: input.competencia ?? null,
          share_with_client: input.share_with_client ?? false,
        })
        .select('id')
        .single();
      if (error) throw error;
      const res = await invokeCode<{ nfse?: { id: string; status: string; rejection_code?: string; rejection_message?: string } }>('nfse-issue', { nfse_id: draft.id });
      if (res.code) throw new Error(res.code);
      // A fn devolve 200 (sem `code`) também em falha fiscal → checar o status do
      // corpo p/ não reportar sucesso quando o Sefin REJEITOU / exigiu reconciliação.
      const st = res.data?.nfse?.status;
      if (st === 'rejected' || st === 'unknown_reconciliation_required') {
        throw new Error(res.data?.nfse?.rejection_message || res.data?.nfse?.rejection_code || 'A emissão não foi autorizada.');
      }
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nfse-documents', companyId] }),
  });

  const cancel = useMutation({
    mutationFn: async (vars: { nfse_id: string; motivo: string; codigo_motivo?: string }) => {
      const res = await invokeCode<{ status?: string; denied_reason?: string }>('nfse-cancel', vars);
      if (res.code) throw new Error(res.code);
      // `denied` volta 200 sem `code` → tratar como falha (o Sefin NEGOU o cancelamento).
      if (res.data?.status === 'denied') {
        throw new Error(res.data?.denied_reason || 'Cancelamento negado pelo provedor.');
      }
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nfse-documents', companyId] }),
  });

  const reconcile = useMutation({
    mutationFn: async (nfse_id: string) => {
      const res = await invokeCode('nfse-reconcile', { nfse_id });
      if (res.code) throw new Error(res.code);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nfse-documents', companyId] }),
  });

  return { companyId, list, issue, cancel, reconcile };
}

/** Certificado A1 ativo da empresa (metadata; usado p/ o alerta de vencimento). */
export function useActiveCertificate() {
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  return useQuery({
    queryKey: ['fiscal-active-cert', companyId],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<FiscalCertificate | null> => {
      const { data, error } = await supabase
        .from('fiscal_certificates')
        .select('*')
        .eq('company_id', companyId as string)
        .eq('status', 'active')
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });
}

/** Timeline (nfse_events) de uma nota. */
export function useNfseEvents(nfseId: string | null) {
  return useQuery({
    queryKey: ['nfse-events', nfseId],
    enabled: Boolean(nfseId),
    queryFn: async (): Promise<NfseEvent[]> => {
      const { data, error } = await supabase
        .from('nfse_events')
        .select('*')
        .eq('nfse_id', nfseId as string)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
}

/** DANFSE: QR + URLs assinadas de download. */
export async function fetchDanfse(nfseId: string): Promise<{
  qr_code_url?: string | null; consulta_url?: string | null;
  xml_nfse_url?: string | null; xml_dps_url?: string | null; pdf_url?: string | null;
} | null> {
  const { data, error } = await supabase.functions.invoke('nfse-danfse', { body: { nfse_id: nfseId } });
  if (error) return null;
  return data as never;
}
